<template>
	<div class="preset-combobox" :class="{ 'is-open': dropdownOpen, 'is-disabled': disabled }">
		<input
			ref="inputRef"
			class="preset-combobox__field"
			type="text"
			role="combobox"
			autocomplete="off"
			:disabled="disabled"
			:value="modelValue"
			:placeholder="placeholder"
			:maxlength="maxLength"
			:aria-label="ariaLabel || undefined"
			:aria-expanded="dropdownOpen"
			:aria-controls="listboxId"
			:aria-activedescendant="activeOptionId || undefined"
			aria-autocomplete="list"
			@input="handleInput"
			@focus="handleFocus"
			@blur="handleBlur"
			@keydown.down.prevent="navigateOptions(1)"
			@keydown.up.prevent="navigateOptions(-1)"
			@keydown.enter.prevent="selectHighlightedOption"
			@keydown.esc.prevent="closeDropdown"
		/>

		<Teleport to="body">
			<div
				v-if="dropdownOpen"
				ref="dropdownRef"
				class="preset-combobox__dropdown"
				:class="{ 'is-above': dropdownPlacement === 'above' }"
				:style="dropdownStyle"
			>
				<div v-if="filteredOptions.length" :id="listboxId" role="listbox">
					<button
						v-for="(option, index) in filteredOptions"
						:id="optionId(index)"
						:key="option"
						type="button"
						class="preset-combobox__option"
						:class="{
							'is-highlighted': highlightedIndex === index,
							'is-selected': isSelected(option),
						}"
						role="option"
						:aria-selected="isSelected(option)"
						@mouseenter="highlightedIndex = index"
						@pointerdown.prevent="selectOption(option)"
					>
						<span>{{ option }}</span>
						<span v-if="isSelected(option)" class="preset-combobox__check" aria-hidden="true">✓</span>
					</button>
				</div>
				<div v-else :id="listboxId" class="preset-combobox__empty" role="status">
					{{ normalizedOptions.length ? noMatchesMessage : emptyMessage }}
				</div>
			</div>
		</Teleport>
	</div>
</template>

<script setup>
import { computed, getCurrentInstance, nextTick, onUnmounted, ref, watch } from 'vue';
import {
	MAX_ASSIGNMENT_SERVICE_AREA_LENGTH,
	normalizeAssignmentServiceAreaOptions,
} from '@/utils/assignmentSubtitlePresets';

const props = defineProps({
	modelValue: {
		type: String,
		default: '',
	},
	options: {
		type: Array,
		default: () => [],
	},
	placeholder: {
		type: String,
		default: 'Enter or select a value',
	},
	ariaLabel: {
		type: String,
		default: '',
	},
	emptyMessage: {
		type: String,
		default: 'No saved values yet',
	},
	noMatchesMessage: {
		type: String,
		default: 'No matching values — you can keep this custom value',
	},
	maxLength: {
		type: Number,
		default: MAX_ASSIGNMENT_SERVICE_AREA_LENGTH,
	},
	disabled: {
		type: Boolean,
		default: false,
	},
});

const emit = defineEmits(['update:modelValue', 'change', 'selected', 'open-change']);

const instanceId = getCurrentInstance()?.uid ?? Math.random().toString(36).slice(2);
const listboxId = `preset-combobox-${instanceId}-listbox`;
const inputRef = ref(null);
const dropdownRef = ref(null);
const dropdownOpen = ref(false);
const dropdownPlacement = ref('below');
const dropdownStyle = ref({});
const query = ref('');
const highlightedIndex = ref(-1);

const normalizedOptions = computed(() => normalizeAssignmentServiceAreaOptions(props.options));
const filteredOptions = computed(() => {
	const search = query.value.trim().toLocaleLowerCase();
	if (!search) return normalizedOptions.value;
	return normalizedOptions.value.filter((option) => option.toLocaleLowerCase().includes(search));
});
const activeOptionId = computed(() =>
	dropdownOpen.value && highlightedIndex.value >= 0
		? optionId(highlightedIndex.value)
		: '',
);

const optionId = (index) => `${listboxId}-option-${index}`;
const normalizeKey = (value) => String(value ?? '').trim().toLocaleLowerCase();
const isSelected = (option) => normalizeKey(option) === normalizeKey(props.modelValue);

const positionDropdown = () => {
	if (!dropdownOpen.value || typeof window === 'undefined') return;
	const rect = inputRef.value?.getBoundingClientRect?.();
	if (!rect) return;
	if (rect.bottom < 0 || rect.top > window.innerHeight) {
		closeDropdown();
		return;
	}

	const fallbackHeight = Math.min(248, Math.max(48, normalizedOptions.value.length * 40 + 8));
	const measuredHeight = dropdownRef.value?.getBoundingClientRect?.().height || fallbackHeight;
	const spaceBelow = window.innerHeight - rect.bottom;
	const spaceAbove = rect.top;
	const placeAbove = spaceBelow < Math.min(measuredHeight, 248) && spaceAbove > spaceBelow;
	const viewportPadding = 8;
	const width = Math.min(Math.max(rect.width, 220), window.innerWidth - viewportPadding * 2);
	const left = Math.min(
		Math.max(rect.left, viewportPadding),
		Math.max(viewportPadding, window.innerWidth - width - viewportPadding),
	);
	const top = placeAbove
		? Math.max(viewportPadding, rect.top - Math.min(measuredHeight, 248) - 5)
		: Math.min(window.innerHeight - viewportPadding, rect.bottom + 5);

	dropdownPlacement.value = placeAbove ? 'above' : 'below';
	dropdownStyle.value = {
		left: `${left}px`,
		top: `${top}px`,
		width: `${width}px`,
	};
};

const addPositionListeners = () => {
	if (typeof window === 'undefined') return;
	window.addEventListener('resize', positionDropdown);
	window.addEventListener('scroll', positionDropdown, true);
};

const removePositionListeners = () => {
	if (typeof window === 'undefined') return;
	window.removeEventListener('resize', positionDropdown);
	window.removeEventListener('scroll', positionDropdown, true);
};

const openDropdown = () => {
	if (props.disabled || dropdownOpen.value) {
		if (dropdownOpen.value) nextTick(positionDropdown);
		return;
	}
	dropdownOpen.value = true;
	highlightedIndex.value = -1;
	emit('open-change', true);
	addPositionListeners();
	nextTick(positionDropdown);
};

const closeDropdown = () => {
	if (!dropdownOpen.value) return;
	dropdownOpen.value = false;
	highlightedIndex.value = -1;
	query.value = '';
	removePositionListeners();
	emit('open-change', false);
};

const handleFocus = () => {
	query.value = '';
	openDropdown();
};

const handleInput = (event) => {
	const value = event.target.value;
	query.value = value;
	emit('update:modelValue', value);
	emit('change', value);
	openDropdown();
};

const handleBlur = () => nextTick(closeDropdown);

const selectOption = (option) => {
	emit('update:modelValue', option);
	emit('change', option);
	emit('selected', option);
	closeDropdown();
	inputRef.value?.blur?.();
};

const navigateOptions = (direction) => {
	if (!dropdownOpen.value) openDropdown();
	if (!filteredOptions.value.length) return;
	const current = highlightedIndex.value;
	highlightedIndex.value =
		current < 0
			? direction > 0 ? 0 : filteredOptions.value.length - 1
			: (current + direction + filteredOptions.value.length) % filteredOptions.value.length;
	nextTick(() => {
		document.getElementById(optionId(highlightedIndex.value))?.scrollIntoView?.({ block: 'nearest' });
	});
};

const selectHighlightedOption = () => {
	if (
		dropdownOpen.value &&
		highlightedIndex.value >= 0 &&
		highlightedIndex.value < filteredOptions.value.length
	) {
		selectOption(filteredOptions.value[highlightedIndex.value]);
		return;
	}
	closeDropdown();
};

const focus = (options) => inputRef.value?.focus?.(options);

watch(
	() => props.options,
	() => {
		if (dropdownOpen.value) nextTick(positionDropdown);
	},
	{ deep: true },
);

defineExpose({ focus, closeDropdown });
onUnmounted(removePositionListeners);
</script>

<style scoped>
.preset-combobox {
	position: relative;
	display: block;
	width: 100%;
	min-width: 0;
}

.preset-combobox__field {
	display: block;
	width: 100%;
	height: var(--proposal-control-height, 38px);
	min-height: var(--proposal-control-height, 38px);
	padding: 6px 12px;
	border: var(--proposal-control-border, 1px solid var(--border-color, #d1d5db));
	border-radius: var(--proposal-control-radius, 8px);
	outline: none;
	background: var(--proposal-control-background, var(--noc-background, #fff));
	color: var(--text-color, #333);
	font: inherit;
	font-size: 0.85rem;
	line-height: 1;
	box-shadow: none;
	box-sizing: border-box;
}

.preset-combobox__field:hover {
	border-color: var(--border-color-active, #9ca3af);
}

.preset-combobox__field:focus,
.preset-combobox__field:focus-visible {
	border: 1px solid var(--noc-blue, #1261c9) !important;
	outline: none;
	box-shadow: none;
}

.preset-combobox__field:disabled {
	opacity: 0.65;
}

.preset-combobox__dropdown {
	position: fixed;
	z-index: 1300;
	max-height: 240px;
	overflow-y: auto;
	padding: 4px;
	border: 1px solid var(--border-color, #d1d5db);
	border-radius: 8px;
	background: var(--noc-background, #fff);
	box-shadow: 0 12px 30px rgba(15, 23, 42, 0.16);
	box-sizing: border-box;
}

.preset-combobox__option {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	width: 100%;
	min-height: 38px;
	padding: 8px 10px;
	border: 0;
	border-radius: 6px;
	background: transparent;
	color: var(--text-color, #333);
	font: inherit;
	font-size: 0.85rem;
	line-height: 1.3;
	text-align: left;
	cursor: pointer;
}

.preset-combobox__option:hover,
.preset-combobox__option.is-highlighted {
	background: var(--noc-background-secondary, #f5f6f8);
}

.preset-combobox__option.is-selected {
	color: var(--primary-color, #1261c9);
	font-weight: 600;
}

.preset-combobox__check {
	flex: 0 0 auto;
}

.preset-combobox__empty {
	padding: 11px 10px;
	color: var(--text-color-secondary, #6b7280);
	font-size: 0.83rem;
	line-height: 1.35;
}
</style>
